import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Centrifuge, type Subscription } from 'centrifuge'
import { api } from './api'

export type Evento = {
  evento: string
  agregado: string
  id: number | null
  datos: Record<string, unknown>
  uuid: string
  creado_en: string
}

export type Presente = { usuario: string; nombre: string }

type Tokens = { token: string; canales: Record<string, string> }
type Oyente = (evento: Evento, canal: string) => void

type Contexto = {
  conectado: boolean
  /** Se suscribe a un canal y devuelve la función para dejar de escucharlo. */
  suscribir: (canal: string, oyente: Oyente, token?: string) => () => void
  presencia: (canal: string) => Promise<Presente[]>
}

const TiempoReal = createContext<Contexto | null>(null)

function urlWebSocket(): string {
  const esquema = window.location.protocol === 'https:' ? 'wss' : 'ws'
  return `${esquema}://${window.location.host}/connection/websocket`
}

/**
 * Un único cliente de Centrifugo para toda la aplicación. Los canales que puede
 * escuchar cada usuario los decide el servidor: aquí solo se usan sus tokens.
 */
export function ProveedorTiempoReal({ children }: { children: ReactNode }) {
  const [conectado, setConectado] = useState(false)
  const clienteRef = useRef<Centrifuge | null>(null)
  const tokensRef = useRef<Record<string, string>>({})
  const oyentesRef = useRef(new Map<string, Set<Oyente>>())
  const subsRef = useRef(new Map<string, Subscription>())
  const [listo, setListo] = useState(false)

  useEffect(() => {
    let cancelado = false

    const arrancar = async () => {
      const tokens = await api<Tokens>('/api/tiempo-real/token')
      if (cancelado) return

      tokensRef.current = tokens.canales
      const cliente = new Centrifuge(urlWebSocket(), {
        token: tokens.token,
        getToken: async () => {
          const nuevos = await api<Tokens>('/api/tiempo-real/token')
          tokensRef.current = nuevos.canales
          return nuevos.token
        },
      })

      cliente.on('connected', () => setConectado(true))
      cliente.on('disconnected', () => setConectado(false))
      cliente.connect()

      clienteRef.current = cliente
      setListo(true)
    }

    void arrancar()

    return () => {
      cancelado = true
      clienteRef.current?.disconnect()
      clienteRef.current = null
      subsRef.current.clear()
    }
  }, [])

  const suscribir = useCallback(
    (canal: string, oyente: Oyente, token?: string) => {
      const oyentes = oyentesRef.current.get(canal) ?? new Set<Oyente>()
      oyentes.add(oyente)
      oyentesRef.current.set(canal, oyentes)

      const cliente = clienteRef.current
      if (cliente && !subsRef.current.has(canal)) {
        const suscripcion =
          cliente.getSubscription(canal) ??
          cliente.newSubscription(canal, {
            token: token ?? tokensRef.current[canal],
            getToken: async () => {
              const nuevos = await api<Tokens>('/api/tiempo-real/token')
              tokensRef.current = nuevos.canales
              return nuevos.canales[canal] ?? token ?? ''
            },
          })

        // Un solo oyente por canal que reparte a todos los interesados.
        suscripcion.on('publication', (ctx) => {
          for (const fn of oyentesRef.current.get(canal) ?? []) {
            fn(ctx.data as Evento, canal)
          }
        })
        suscripcion.subscribe()
        subsRef.current.set(canal, suscripcion)
      }

      return () => {
        const restantes = oyentesRef.current.get(canal)
        restantes?.delete(oyente)
        if (restantes && restantes.size === 0) {
          oyentesRef.current.delete(canal)
          subsRef.current.get(canal)?.unsubscribe()
          subsRef.current.delete(canal)
        }
      }
    },
    [],
  )

  const presencia = useCallback(async (canal: string): Promise<Presente[]> => {
    const suscripcion = subsRef.current.get(canal)
    if (!suscripcion) return []

    try {
      const resultado = await suscripcion.presence()
      return Object.values(resultado.clients).map((cliente) => ({
        usuario: cliente.user,
        nombre: (cliente.connInfo as { nombre?: string } | undefined)?.nombre ?? 'Alguien',
      }))
    } catch {
      return []
    }
  }, [])

  const valor = useMemo(
    () => ({ conectado, suscribir, presencia }),
    [conectado, suscribir, presencia, listo],
  )

  return <TiempoReal.Provider value={valor}>{children}</TiempoReal.Provider>
}

export function useTiempoReal(): Contexto {
  const contexto = useContext(TiempoReal)
  if (!contexto) throw new Error('Falta el ProveedorTiempoReal')
  return contexto
}

/** Escucha un canal mientras el componente esté montado. */
export function useCanal(canal: string | null, oyente: Oyente, token?: string) {
  const { suscribir } = useTiempoReal()
  const oyenteRef = useRef(oyente)
  oyenteRef.current = oyente

  useEffect(() => {
    if (!canal) return
    return suscribir(canal, (evento, nombre) => oyenteRef.current(evento, nombre), token)
  }, [canal, token, suscribir])
}

/** Quién está ahora mismo dentro de un canal. */
export function usePresencia(canal: string | null): Presente[] {
  const { presencia } = useTiempoReal()
  const [gente, setGente] = useState<Presente[]>([])

  const refrescar = useCallback(() => {
    if (!canal) return
    void presencia(canal).then(setGente)
  }, [canal, presencia])

  useCanal(canal, refrescar)

  useEffect(() => {
    refrescar()
    // La presencia también cambia sin que haya publicaciones: se repasa cada poco.
    const reloj = setInterval(refrescar, 15000)
    return () => clearInterval(reloj)
  }, [refrescar])

  return gente
}
